import {
  EXPANDED_EDITOR_FOOTER,
  EXPANDED_EDITOR_ACTIONS,
} from "../expanded-editor-footer.js";
import { useId, useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  COUNTERMEASURE_TABLE_MAX_ROWS,
  CountermeasureRowSchema,
  CountermeasureStatus,
  OrdinalBand,
  widgetTargetKey,
  type CountermeasureRow,
  type CountermeasureTableParams,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/index.js";
import { Select } from "../../molecules/Select/Select.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { WidgetPersonField } from "../person-field.js";
import type { WidgetChangeOptions } from "../types.js";
import { usePeopleSource } from "../user-directory.js";

export function CountermeasureDetails({
  row,
  data,
  isNew,
  readOnly,
  onDirty,
  onSave,
  onDelete,
  onCancel,
}: {
  row: CountermeasureRow;
  data: CountermeasureTableParams;
  isNew: boolean;
  readOnly: boolean;
  onDirty: () => void;
  /** With `grantViewerIfMissing`, a promise that settles with the save. */
  onSave: (
    row: CountermeasureRow,
    options?: WidgetChangeOptions,
  ) => void | Promise<void>;
  onDelete: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<CountermeasureRow>();
  const [baseline, setBaseline] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const people = usePeopleSource();
  const windowId = useId();
  const windowHintId = useId();
  const shown = draft ?? row;
  // A person picked in this draft, not the one already saved: Save may add
  // them as Viewer (d4 — forms grant on Save, and Cancel adds nobody).
  const pickedNew =
    !!shown.ownerUserId && shown.ownerUserId !== row.ownerUserId;
  // The save's own optimistic echo is not someone else's change.
  const conflict =
    !saving && !!draft && !isNew && JSON.stringify(row) !== baseline;
  const atLimit = isNew && data.rows.length >= COUNTERMEASURE_TABLE_MAX_ROWS;
  /**
   * PB-4923 AC5. Six independent leaves on one row, and the two selects claim
   * as well as the three text fields — see `SelectProps.onFocus` for why a
   * dropdown is not exempt. A NEW row leases nothing: it has no server row, so
   * a claim would name a target no operation could address.
   */
  const target = (field: string): string | undefined =>
    isNew
      ? undefined
      : widgetTargetKey([
          { kind: "item", collection: "rows", itemId: row.id },
          { kind: "prop", name: field },
        ]);
  const titleLease = useFieldEditSession(target("title"));
  const ownerLease = useFieldEditSession(target("owner"));
  const statusLease = useFieldEditSession(target("status"));
  const windowLease = useFieldEditSession(target("window"));
  const impactLease = useFieldEditSession(target("impact"));
  const effortLease = useFieldEditSession(target("effort"));
  const bandLease = { impact: impactLease, effort: effortLease } as const;
  const update = (patch: Partial<CountermeasureRow>) => {
    if (!draft) setBaseline(JSON.stringify(row));
    setDraft({ ...shown, ...patch });
    setSaveError(null);
    onDirty();
  };
  const inputClass =
    "w-full min-w-0 rounded-md border border-pb-border bg-pb-card-elevated px-3 py-2.5 text-sm text-pb-text focus-ring disabled:opacity-70";
  const save = () => {
    if (
      readOnly ||
      saving ||
      conflict ||
      atLimit ||
      !CountermeasureRowSchema.safeParse(shown).success
    )
      return;
    const saved = onSave(
      shown,
      pickedNew ? { grantViewerIfMissing: true } : undefined,
    );
    if (!(saved instanceof Promise)) return;
    // A refused save keeps the draft and says why under Owner.
    setSaving(true);
    saved.catch((reason: unknown) => {
      setSaving(false);
      setSaveError(
        reason instanceof Error && reason.message
          ? reason.message
          : "Edit not saved",
      );
    });
  };
  return (
    <form
      className="@container flex min-h-full flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <h3 className="text-sm font-medium text-pb-text">
        {isNew ? "New countermeasure" : "Countermeasure details"}
      </h3>
      <label className="space-y-2 text-sm text-pb-text-muted">
        Countermeasure
        <input
          className={inputClass}
          value={shown.title}
          maxLength={255}
          disabled={readOnly}
          {...titleLease}
          onChange={(event) => update({ title: event.target.value })}
        />
      </label>
      <div className="grid gap-4 @lg:grid-cols-2">
        {people ? (
          <WidgetPersonField
            source={people}
            label="Owner"
            allowTyped={false}
            text={shown.owner}
            userId={shown.ownerUserId}
            timing="save"
            pendingGrant={pickedNew}
            error={saveError}
            readOnly={readOnly}
            {...ownerLease}
            onChange={(next) =>
              update({ owner: next.text, ownerUserId: next.userId })
            }
          />
        ) : (
          <label className="space-y-2 text-sm text-pb-text-muted">
            Owner
            <input
              className={inputClass}
              aria-label="Owner"
              value={shown.owner}
              maxLength={255}
              disabled={readOnly}
              readOnly
            />
            <span className="block text-dense-1 text-pb-text-muted">
              People search is unavailable here.
            </span>
          </label>
        )}
        <Select
          label="Status"
          ariaLabel="Status"
          value={shown.status}
          disabled={readOnly}
          {...statusLease}
          options={CountermeasureStatus.options.map((status) => ({
            value: status,
            label: status,
          }))}
          onChange={(status) =>
            update({ status: status as CountermeasureRow["status"] })
          }
        />
      </div>
      <div className="space-y-2 text-sm text-pb-text-muted">
        <label htmlFor={windowId}>Time window</label>
        <input
          id={windowId}
          aria-describedby={windowHintId}
          className={inputClass}
          value={shown.window}
          maxLength={255}
          disabled={readOnly}
          {...windowLease}
          onChange={(event) => update({ window: event.target.value })}
        />
        <span id={windowHintId} className="block text-xs">
          For example: 2 wks or Q3
        </span>
      </div>
      <details className="group border-y border-pb-border py-4">
        <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-sm text-pb-text">
          <ChevronRight className="h-4 w-4 group-open:rotate-90" />
          <span>Impact &amp; effort</span>
          <span className="ms-auto text-xs text-pb-text-muted">
            {shown.impact} impact · {shown.effort} effort
          </span>
        </summary>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {(["impact", "effort"] as const).map((field) => (
            <Select
              key={field}
              label={field === "impact" ? "Impact" : "Effort"}
              ariaLabel={field === "impact" ? "Impact" : "Effort"}
              value={shown[field]}
              disabled={readOnly}
              {...bandLease[field]}
              options={OrdinalBand.options.map((band) => ({
                value: band,
                label: band,
              }))}
              onChange={(band) =>
                update({ [field]: band as CountermeasureRow["impact"] })
              }
            />
          ))}
        </div>
      </details>
      {conflict && (
        <p role="alert" className="text-sm text-pb-text">
          This countermeasure changed while you were editing. Cancel to reload
          the latest version.
        </p>
      )}
      {atLimit && (
        <p role="alert" className="text-sm text-pb-text">
          The plan has reached its limit of {COUNTERMEASURE_TABLE_MAX_ROWS}{" "}
          countermeasures.
        </p>
      )}
      {!readOnly && (
        <div className={EXPANDED_EDITOR_FOOTER}>
          {!isNew && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-pb-red-text"
              disabled={!!draft}
              onClick={onDelete}
            >
              Delete countermeasure
            </Button>
          )}
          <div className={EXPANDED_EDITOR_ACTIONS}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={(!draft && !isNew) || conflict || atLimit || saving}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
